import { EditMeeting, GetMeeting, RegisterMeeting } from "@/dtos/Meeting.dto";
import { StateObject } from "@/dtos/State.dto";
import Meeting from "@/services/meeting";
import {
  CLEAR_MEETING_ISLOADING,
  DELETE_MEETING,
  EDIT_MEETING,
  GENERATE_MEETING_LINK,
  GET_MEETING,
  GET_MEETINGS,
  GET_MEETING_STATS,
  REGISTER_MEETING,
} from "@/store/action-types";
import { SET_ERRORMSG, SET_MEETING, SET_MEETINGS, SET_MEETING_STATS } from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const meeting: Module<any, {}> = {
  state: {
    meeting: {
      data: null,
      isLoading: false,
    },
    meetings: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    meetingStats: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    meeting: (state: { meeting: any }) => state.meeting,
    meetings: (state: { meetings: any }) => state.meetings,
    meetingStats: (state: { meetingStats: any }) => state.meetingStats,
  },
  mutations: {
    [SET_MEETING]: (
      state: { meeting: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.meeting.data = payload.data;
      state.meeting.isLoading = payload.isLoading;
    },
    [SET_MEETINGS]: (
      state: { meetings: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.meetings.data = payload.data;
      if (payload.metaData != null) state.meetings.metaData = payload.metaData;
      state.meetings.isLoading = payload.isLoading;
    },
    [SET_MEETING_STATS]: (
      state: { meetingStats: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.meetingStats.data = payload.data;
      state.meetingStats.isLoading = payload.isLoading;
    },
  },
  actions: {
    [GENERATE_MEETING_LINK]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETING, { isLoading: true });

      const result: AxiosResponse = await Meeting.generateMeetingLink(payload as RegisterMeeting);

      if (result) {
        if (result.data.status) {
          commit(SET_MEETING, { isLoading: false });
          return result.data.data;
        }
        commit(SET_MEETING, { isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_MEETING, { isLoading: false });
        return false;
      }
    },
    [REGISTER_MEETING]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETING, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Meeting.registerMeeting(payload as RegisterMeeting);

        if (result) {
          if (result.data.status) {
            commit(SET_MEETING, { data: result.data.data, isLoading: false });
            return result.data.data;
          }
          commit(SET_MEETING, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_MEETING, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_MEETING, { data: null, isLoading: false });
        const errorMessage =
          error?.response?.data?.message || "Failed to register meeting. Please try again.";
        commit(SET_ERRORMSG, errorMessage);
        return false;
      }
    },
    [GET_MEETINGS]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETINGS, { data: null, isLoading: true });

      const result: AxiosResponse = await Meeting.getMeetings(payload as GetMeeting);

      if (result) {
        if (result.data.status) {
          commit(SET_MEETINGS, {
            data: result.data.data,
            metaData: result.data.metaData,
            isLoading: false,
          });
          return true;
        }
        commit(SET_MEETINGS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_MEETINGS, { isLoading: false });
        return false;
      }
    },
    [GET_MEETING]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETING, { data: null, isLoading: true });

      const result: AxiosResponse = await Meeting.getMeetings(payload as GetMeeting);

      if (result) {
        if (result.data.status) {
          commit(SET_MEETING, {
            data: result.data.data[0],
            isLoading: false,
          });
          return result.data.data?.[0];
        }
        commit(SET_MEETING, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_MEETING, { isLoading: false });
        return false;
      }
    },
    [GET_MEETING_STATS]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETING_STATS, { data: null, isLoading: true });

      const result: AxiosResponse = await Meeting.getMeetingStats(payload as GetMeeting);

      if (result) {
        if (result.data.status) {
          commit(SET_MEETING_STATS, {
            data: result.data.data,
            metaData: result.data.metaData,
            isLoading: false,
          });
          return true;
        }
        commit(SET_MEETING_STATS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_MEETING_STATS, { isLoading: false });
        return false;
      }
    },
    [EDIT_MEETING]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETING, { data: null, isLoading: true });

      const result: AxiosResponse = await Meeting.editMeeting(payload as EditMeeting);

      if (result) {
        if (result.data.status) {
          commit(SET_MEETING, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_MEETING, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_MEETING, { isLoading: false });
        return false;
      }
    },
    [DELETE_MEETING]: async ({ commit }: any, payload: string | string[]) => {
      commit(SET_MEETING, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Meeting.deleteMeeting(payload);

        if (result && result.data) {
          if (result.data.status && result.status === 200) {
            commit(SET_MEETING, { data: result.data.data, isLoading: false });
            return {
              success: true,
              message: result.data.data?.message || "Meeting(s) deleted successfully",
            };
          }
          commit(SET_MEETING, { data: null, isLoading: false });
          const errorMessage =
            result.data.message || result.data.msg || "Failed to delete meeting(s)";
          commit(SET_ERRORMSG, errorMessage);
          return { success: false, message: errorMessage };
        } else {
          commit(SET_MEETING, { isLoading: false });
          return { success: false, message: "Failed to delete meeting(s)" };
        }
      } catch (error: any) {
        commit(SET_MEETING, { data: null, isLoading: false });
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message ||
          "An error occurred while deleting the meeting(s)";
        commit(SET_ERRORMSG, errorMessage);
        return { success: false, message: errorMessage };
      }
    },
    [CLEAR_MEETING_ISLOADING]: ({ commit }) => {
      commit(SET_MEETING, { isLoading: false });
      commit(SET_MEETINGS, { isLoading: false });
    },
  },
};

export default meeting;
